import React, { useEffect, useState } from 'react';

import { Collapse } from '@material-ui/core';
import { ChevronRight, KeyboardArrowDown } from '@material-ui/icons';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import clsx from 'clsx';
import { format, subDays } from 'date-fns';
import { FastField, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import ContentLoader from 'react-content-loader';
import { useInView } from 'react-intersection-observer';

import { useGetRecipesQuery } from '../../../../../../api/menu';
import { useGeIsTeammateViewer } from '../../../../../../api/teammates/hooks';
import { useCreateWastageMutation } from '../../../../../../api/wastages';
import { useGetWastagesList } from '../../../../../../api/wastages/hooks';
import { WastageReason, WastageSortBy, WastageType } from '../../../../../../api/wastages/types';
import { ThemedButton } from '../../../../../../shared/components/themed-button';
import { DatePickerComponent } from '../../../../../../shared/components/date-picker';
import { IDateRange } from '../../../../../../shared/components/date-range';
import { DropdownMenu } from '../../../../../../shared/components/dropdown-menu';
import { FilterButton } from '../../../../../../shared/components/filter-button';
import { FormikInput } from '../../../../../../shared/components/formik-input';
import { FormikSortDropdown } from '../../../../../../shared/components/formik-sort-dropdown';
import { RangeDropdown, RangeOptions } from '../../../../../../shared/components/range-dropdown';
import { CustomTooltip } from '../../../../../../shared/components/tooltip';
import { useScreenSize } from '../../../../../../shared/hooks/use-screen-size';
import { LocalStorageService } from '../../../../../../shared/services/localStorage.service';
import { ToastService } from '../../../../../../shared/services/toastService';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { getWastageSortFilters, wastageSelectors } from '../../../../../../store/wastage';
import { setWastageSortFilters } from '../../../../../../store/wastage/wastage.actions';
import { colorVariables } from '../../../../../../styles/colorVariables';
import { SearchWastageItem } from './search-wastage-item';
import { SearchWastageItemById } from './search-wastage-item-byid';
import { WastageTableRow } from './WastageTableRow';
import { WasteFilter } from './waste-filter';

import { useStyles } from './style';

export const wastagesSortParams = [
  { label: 'Date (Most recent to Old)', value: 'wastage_date desc' },
  { label: 'Date (Old to Most recent)', value: 'wastage_date asc' },
  { label: 'Product/Recipe (A - Z)', value: 'name asc' },
  { label: 'Product/Recipe (Z - A)', value: 'name desc' },
  { label: 'Product/Recipe ID (A - Z)', value: 'recipe_id asc' },
  { label: 'Product/Recipe ID (Z - A)', value: 'recipe_id desc' },
  { label: 'Quantity (Low - High)', value: 'quantity asc' },
  { label: 'Quantity (High - Low)', value: 'quantity desc' },
  { label: 'Reason (A - Z)', value: 'reason asc' },
  { label: 'Reason (Z - A)', value: 'reason desc' },
  { label: 'Reporter (A - Z)', value: 'reporter_name asc' },
  { label: 'Reporter (Z - A)', value: 'reporter_name desc' },
  { label: 'Type (A - Z)', value: 'wastage_type asc' },
  { label: 'Type (Z - A)', value: 'wastage_type desc' },
];

export const wastageReasons = [
  { label: 'Dropped/Broken', value: '0' },
  { label: 'Expired/Spoiled', value: '1' },
  { label: 'Personal use', value: '2' },
  { label: 'Promotion/Testing', value: '3' },
  { label: 'On the house', value: '4' },
];

export const WastagePage: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { isMobile } = useScreenSize();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [addNewExpanded, setAddNewExpanded] = useState(false);
  const [isFilterOpened, setIsFilterOpened] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<{
    id: number;
    type: WastageType;
    name: string;
    code: string;
  } | null>(null);
  const [inViewRef, inView] = useInView();

  const { filter, sortBy, currentPage } = useAppSelector(getWastageSortFilters);
  const { loading } = useGetWastagesList(inView);
  const list = useAppSelector(wastageSelectors.selectAll);
  const isViewer = useGeIsTeammateViewer('sales_and_wastages');

  const [createWastage, { isLoading: createLoading }] = useCreateWastageMutation();
  useGetRecipesQuery({});

  const onAddWastage = (
    { qty, reason, reporter }: { qty: number; reporter: string; reason: string },
    formikHelpers: FormikHelpers<any>,
  ) => {
    formikHelpers.setSubmitting(false);
    if (isViewer) {
      ToastService.error('You have been restricted from making edits.');
      return;
    }
    selectedRecipe &&
      createWastage({
        wastage: {
          wastage_date: (selectedDate || new Date()).toString(),
          waste_id: selectedRecipe.id,
          quantity: qty,
          reason: +reason as WastageReason,
          wastage_type: selectedRecipe.type,
          reporter_name: reporter,
        },
      });
    formikHelpers.resetForm(undefined);
    setSelectedDate(null);
    setSelectedRecipe(null);
  };

  const onCancel = () => {
    setSelectedDate(null);
    setSelectedRecipe(null);
    setAddNewExpanded(false);
  };

  const setWastageRange = (range: RangeOptions) => {
    if (range !== RangeOptions.CUSTOM) {
      dispatch(setWastageSortFilters({ filter: { selectedRange: range, menu: filter?.menu }, sortBy }));
    }
  };

  const onApplySort = (param: WastageSortBy) => {
    dispatch(setWastageSortFilters({ sortBy: param, filter }));
  };

  const setWastageDateRange = (dateRange: IDateRange) => {
    dispatch(
      setWastageSortFilters({
        filter: {
          fromDate: dateRange.startDate?.toDateString(),
          toDate: dateRange.endDate?.toDateString(),
          selectedRange: dateRange.startDate ? RangeOptions.CUSTOM : filter?.selectedRange || RangeOptions.TODAY,
          menu: filter?.menu,
        },
        sortBy,
      }),
    );
  };

  const toggleAddNewExpanded = () => {
    setAddNewExpanded((prev) => !prev);
  };

  const handleToggleFilter = () => {
    setIsFilterOpened((prev) => !prev);
  };

  useEffect(() => {
    LocalStorageService.getItem('wastage_range') &&
      dispatch(setWastageSortFilters({ filter: { selectedRange: LocalStorageService.getItem('wastage_range') } }));
    LocalStorageService.clear('wastage_range');
  }, []);

  const FilterToolTip = (
    <CustomTooltip
      right={isMobile ? 0 : -45}
      onClose={setIsFilterOpened.bind(null, false)}
      triggerBtn={<FilterButton color={colorVariables.grey60} onClick={handleToggleFilter} />}
      clickOutsideUnTrackable={true}
      isOpen={isFilterOpened}
    >
      <WasteFilter handleCloseModal={setIsFilterOpened.bind(null, false)} />
    </CustomTooltip>
  );

  return (
    <div className={classes.box}>
      <div className={classes.headAction}>
        <div className={classes.filtersBlock}>
          <RangeDropdown
            defaultRange={filter?.selectedRange || RangeOptions.TODAY}
            triggerClass={classes.dropdownTrigger}
            setRange={setWastageRange}
            setDateRange={setWastageDateRange}
            skipDateUpdate={true}
          />
        </div>
        <div className={classes.filterIcon}>{FilterToolTip}</div>
      </div>
      <div className={classes.tableHead}>
        <div className={classes.date}>
          <DropdownMenu
            items={[
              <div key='asc' onClick={onApplySort.bind(null, ['wastage_date', 'desc'])}>
                Most recent to Old
              </div>,
              <div key='desc' onClick={onApplySort.bind(null, ['wastage_date', 'asc'])}>
                Old to Most recent
              </div>,
            ]}
            triggerBtn={
              <div className={classes.sortTrigger}>
                <span className={classes.colHeadText}>Date</span>
                <KeyboardArrowDown />
              </div>
            }
          />
        </div>
        <div className={classes.recipe}>
          <DropdownMenu
            items={[
              <div key='asc' onClick={onApplySort.bind(null, ['name', 'asc'])}>
                Sort A-Z
              </div>,
              <div key='desc' onClick={onApplySort.bind(null, ['name', 'desc'])}>
                Sort Z-A
              </div>,
            ]}
            triggerBtn={
              <div className={classes.sortTrigger}>
                <span className={classes.colHeadText}> Product/Recipe</span>
                <KeyboardArrowDown />
              </div>
            }
          />
        </div>
        <div className={classes.recipeId}>
          <DropdownMenu
            items={[
              <div key='asc' onClick={onApplySort.bind(null, ['recipe_id', 'asc'])}>
                Sort A-Z
              </div>,
              <div key='desc' onClick={onApplySort.bind(null, ['recipe_id', 'desc'])}>
                Sort Z-A
              </div>,
            ]}
            triggerBtn={
              <div className={classes.sortTrigger}>
                <span className={classes.colHeadText}> Product/Recipe ID</span>
                <KeyboardArrowDown />
              </div>
            }
          />
        </div>
        <div className={classes.sold}>
          <DropdownMenu
            items={[
              <div key='asc' onClick={onApplySort.bind(null, ['quantity', 'asc'])}>
                Low to High
              </div>,
              <div key='desc' onClick={onApplySort.bind(null, ['quantity', 'desc'])}>
                High to Low
              </div>,
            ]}
            triggerBtn={
              <div className={classes.sortTrigger}>
                <span className={classes.colHeadText}> Quantity</span>
                <KeyboardArrowDown />
              </div>
            }
          />
        </div>
        <div className={classes.reason}>
          <DropdownMenu
            items={[
              <div key='asc' onClick={onApplySort.bind(null, ['reason', 'asc'])}>
                Sort A-Z
              </div>,
              <div key='desc' onClick={onApplySort.bind(null, ['reason', 'desc'])}>
                Sort Z-A
              </div>,
            ]}
            triggerBtn={
              <div className={classes.sortTrigger}>
                <span className={classes.colHeadText}> reason</span>
                <KeyboardArrowDown />
              </div>
            }
          />
        </div>
        <div className={classes.reporter}>
          <DropdownMenu
            items={[
              <div key='asc' onClick={onApplySort.bind(null, ['reporter_name', 'asc'])}>
                Sort A-Z
              </div>,
              <div key='desc' onClick={onApplySort.bind(null, ['reporter_name', 'desc'])}>
                Sort Z-A
              </div>,
            ]}
            triggerBtn={
              <div className={classes.sortTrigger}>
                <span className={classes.colHeadText}> Reporter</span>
                <KeyboardArrowDown />
              </div>
            }
          />
        </div>
        <div className={classes.type}>
          <DropdownMenu
            items={[
              <div key='asc' onClick={onApplySort.bind(null, ['wastage_type', 'asc'])}>
                Sort A-Z
              </div>,
              <div key='desc' onClick={onApplySort.bind(null, ['wastage_type', 'desc'])}>
                Sort Z-A
              </div>,
            ]}
            triggerBtn={
              <div className={classes.sortTrigger}>
                <span className={classes.colHeadText}>Type</span>
                <KeyboardArrowDown />
              </div>
            }
          />
        </div>
        <div className={classes.actions} />
      </div>
      <div className={classes.list}>
        {isMobile ? (
          <div className={classes.fixedHead}>
            <RangeDropdown
              defaultRange={filter?.selectedRange || RangeOptions.TODAY}
              triggerClass={classes.dropdownTrigger}
              setRange={setWastageRange}
              setDateRange={setWastageDateRange}
              skipDateUpdate={true}
            />
            <div className={classes.addNewSaleM}>
              <div className={classes.addSaleBtnM} onClick={toggleAddNewExpanded}>
                <span>ADD NEW WASTAGE</span>
                <ChevronRight className={addNewExpanded ? classes.addChevronRotated : undefined} />
              </div>
              <Collapse in={addNewExpanded} timeout={500}>
                <Formik initialValues={{ qty: 0, reporter: '', reason: ' ' }} onSubmit={onAddWastage} enableReinitialize={true}>
                  {({ submitForm, values }) => (
                    <Form className={classes.addNewBox}>
                      <div className={classes.addNewDateWrap}>
                        <div className={classes.dateWrap}>
                          <DatePickerComponent
                            onDateChange={(date) => {
                              setSelectedDate(date);
                            }}
                            selectedDate={selectedDate}
                            minDate={subDays(new Date(), 360)}
                            trigger={
                              <div className={classes.dateTrigger}>
                                {selectedDate ? format(selectedDate, 'd/MM/y') : 'Today'}
                                <KeyboardArrowDownIcon />
                              </div>
                            }
                          />
                        </div>
                        <div className={classes.recipeId}>
                          <SearchWastageItemById
                            setRecipe={(recipe) => {
                              setSelectedRecipe(recipe);
                            }}
                            selectedRecipe={selectedRecipe}
                          />
                        </div>
                      </div>
                      <div className={classes.recipe}>
                        <SearchWastageItem
                          setRecipe={(recipe) => {
                            setSelectedRecipe(recipe);
                          }}
                          selectedRecipe={selectedRecipe}
                        />
                      </div>
                      <div className={classes.addNewMRows}>
                        <div>Quantity</div>
                        <div>
                          <div className={classes.cellInputWrap}>
                            <FastField name='qty'>
                              {(fieldProps: FieldProps) => (
                                <FormikInput {...fieldProps} placeholder={`0`} label='' height={40} type='number' />
                              )}
                            </FastField>
                          </div>
                        </div>
                      </div>
                      <div className={classes.addNewMRows}>
                        <div>Reporter</div>
                        <div>
                          <div className={classes.cellInputWrapReporter}>
                            <FastField name='reporter'>
                              {(fieldProps: FieldProps) => (
                                <FormikInput {...fieldProps} placeholder={`Enter Name`} label='' height={24} inputFontSize={14} />
                              )}
                            </FastField>
                          </div>
                        </div>
                      </div>
                      <div className={classes.addNewMRows}>
                        <div>REASON</div>
                        <div>
                          <div className={classes.reason}>
                            <FormikSortDropdown
                              name='reason'
                              options={wastageReasons}
                              customSelectFormControlClass={classes.formControl}
                              placeholder={'Select Reason'}
                              submitOnChange={false}
                            />
                          </div>
                        </div>
                      </div>
                      <div className={classes.actionsM}>
                        <ThemedButton
                          onClick={onCancel}
                          title='Cancel'
                          buttonStyle='secondary'
                          className={classes.addActionBtn}
                          width={98}
                          type='reset'
                        />
                        <ThemedButton
                          onClick={submitForm}
                          title='Add'
                          className={classes.addActionBtn}
                          width={98}
                          disabled={!+values.qty || !selectedRecipe || createLoading || !values.reason.trim()}
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </Collapse>
            </div>
          </div>
        ) : (
          <Formik initialValues={{ qty: 0, reporter: '', reason: ' ' }} onSubmit={onAddWastage} enableReinitialize={true}>
            {({ submitForm, values }) => (
              <Form className={classes.addRow}>
                <div className={classes.date}>
                  <div className={classes.dateWrap}>
                    <DatePickerComponent
                      onDateChange={(date) => {
                        setSelectedDate(date);
                      }}
                      selectedDate={selectedDate}
                      minDate={subDays(new Date(), 360)}
                      trigger={
                        <div className={classes.dateTrigger}>
                          {selectedDate ? format(selectedDate, 'd/MM/y') : 'Today'}
                          <KeyboardArrowDownIcon />
                        </div>
                      }
                    />
                  </div>
                </div>
                <div className={classes.recipe}>
                  <SearchWastageItem
                    setRecipe={(recipe) => {
                      setSelectedRecipe(recipe);
                    }}
                    selectedRecipe={selectedRecipe}
                  />
                </div>
                <div className={classes.recipeId}>
                  <SearchWastageItemById
                    setRecipe={(recipe) => {
                      setSelectedRecipe(recipe);
                    }}
                    selectedRecipe={selectedRecipe}
                  />
                </div>
                <div className={classes.sold}>
                  <div className={classes.cellInputWrap}>
                    <FastField name='qty'>
                      {(fieldProps: FieldProps) => <FormikInput {...fieldProps} placeholder={`0`} label='' height={40} type='number' />}
                    </FastField>
                  </div>
                </div>
                <div className={classes.reason}>
                  <FormikSortDropdown
                    name='reason'
                    options={wastageReasons}
                    customSelectFormControlClass={classes.formControl}
                    placeholder={'Select Reason'}
                    submitOnChange={false}
                  />
                </div>
                <div className={classes.reporter}>
                  <div className={classes.cellInputWrapReporter}>
                    <FastField name='reporter'>
                      {(fieldProps: FieldProps) => (
                        <FormikInput {...fieldProps} placeholder={`Enter Name`} label='' height={24} inputFontSize={14} />
                      )}
                    </FastField>
                  </div>
                </div>
                <div className={clsx(classes.type, classes.addBtnCell)}>
                  <ThemedButton
                    onClick={onCancel}
                    title='Cancel'
                    buttonStyle='secondary'
                    className={classes.addActionBtn}
                    width={98}
                    type='reset'
                  />
                  <ThemedButton
                    onClick={submitForm}
                    title='Add'
                    className={classes.addActionBtn}
                    width={98}
                    disabled={!+values.qty || !selectedRecipe || createLoading || !values.reason.trim()}
                  />
                </div>
                <div className={classes.actions} />
              </Form>
            )}
          </Formik>
        )}
        {loading && currentPage === 1
          ? new Array(15).fill(1).map((_, idx) => <LoadingRow key={idx} />)
          : list.map((item, idx, arr) => (
              <div key={item.id} ref={idx === arr.length - 10 ? inViewRef : undefined}>
                <WastageTableRow item={item} />
              </div>
            ))}
        {loading && currentPage > 1 && new Array(15).fill(1).map((_, idx) => <LoadingRow key={idx} />)}
      </div>
    </div>
  );
};

const LoadingRow = () => {
  const classes = useStyles();
  const { isMobile } = useScreenSize();

  return isMobile ? (
    <div className={classes.loadingRow}>
      <div>
        <ContentLoader
          title={''}
          speed={1.8}
          width={200}
          height={13}
          backgroundColor='rgba(152, 165, 183, 0.3)'
          foregroundColor='rgba(152, 165, 183, 0.15)'
        >
          <rect x='0' y='0' rx='7' ry='7' width='200' height='13' />
        </ContentLoader>
      </div>
      <div>
        <ContentLoader
          title={''}
          speed={1.8}
          width={100}
          height={13}
          backgroundColor='rgba(152, 165, 183, 0.2)'
          foregroundColor='rgba(152, 165, 183, 0.05)'
        >
          <rect x='0' y='0' rx='7' ry='7' width='100' height='13' />
        </ContentLoader>
      </div>
      <div className={classes.bottomSkeletonRow}>
        <div>
          <ContentLoader
            title={''}
            speed={1.8}
            width={100}
            height={13}
            backgroundColor='rgba(152, 165, 183, 0.3)'
            foregroundColor='rgba(152, 165, 183, 0.15)'
          >
            <rect x='0' y='0' rx='7' ry='7' width='100' height='13' />
          </ContentLoader>
        </div>
        <div>
          <ContentLoader
            title={''}
            speed={1.8}
            width={100}
            height={13}
            backgroundColor='rgba(152, 165, 183, 0.15)'
            foregroundColor='rgba(152, 165, 183, 0.05)'
          >
            <rect x='0' y='0' rx='7' ry='7' width='100' height='13' />
          </ContentLoader>
        </div>
        <div>
          <ContentLoader
            title={''}
            speed={1.8}
            width={100}
            height={13}
            backgroundColor='rgba(152, 165, 183, 0.3)'
            foregroundColor='rgba(152, 165, 183, 0.15)'
          >
            <rect x='0' y='0' rx='7' ry='7' width='100' height='13' />
          </ContentLoader>
        </div>
      </div>
    </div>
  ) : (
    <div className={classes.loadingRow}>
      <div className={classes.date}>
        <div>
          <div>
            <ContentLoader
              title={''}
              speed={1.8}
              width={74}
              height={13}
              backgroundColor='rgba(152, 165, 183, 0.3)'
              foregroundColor='rgba(152, 165, 183, 0.1)'
            >
              <rect x='0' y='0' rx='7' ry='7' width='74' height='13' />
            </ContentLoader>
          </div>
          <div>
            <ContentLoader
              title={''}
              speed={1.8}
              width={63}
              height={13}
              backgroundColor='rgba(152, 165, 183, 0.15)'
              foregroundColor='rgba(152, 165, 183, 0.05)'
            >
              <rect x='0' y='0' rx='7' ry='7' width='63' height='13' />
            </ContentLoader>
          </div>
        </div>
      </div>
      <div className={classes.recipe}>
        <div>
          <div>
            <ContentLoader
              title={''}
              speed={1.8}
              width={'90%'}
              height={13}
              backgroundColor='rgba(152, 165, 183, 0.3)'
              foregroundColor='rgba(152, 165, 183, 0.1)'
            >
              <rect x='0' y='0' rx='7' ry='7' width='90%' height='13' />
            </ContentLoader>
          </div>
          <div>
            <ContentLoader
              title={''}
              speed={1.8}
              width={'60%'}
              height={13}
              backgroundColor='rgba(152, 165, 183, 0.15)'
              foregroundColor='rgba(152, 165, 183, 0.05)'
            >
              <rect x='0' y='0' rx='7' ry='7' width='60%' height='13' />
            </ContentLoader>
          </div>
        </div>
      </div>
      <div className={classes.recipeId}>
        <div className={classes.centeredSkeleton}>
          <ContentLoader
            title={''}
            speed={1.8}
            width={'90%'}
            height={13}
            backgroundColor='rgba(152, 165, 183, 0.15)'
            foregroundColor='rgba(152, 165, 183, 0.05)'
          >
            <rect x='0' y='0' rx='7' ry='7' width='90%' height='13' />
          </ContentLoader>
        </div>
      </div>
      <div className={classes.sold}>
        <div className={classes.cellEmpty} />
      </div>
      <div className={classes.reason}>
        <div className={classes.centeredSkeleton}>
          <ContentLoader
            title={''}
            speed={1.8}
            width={80}
            height={13}
            backgroundColor='rgba(152, 165, 183, 0.2)'
            foregroundColor='rgba(152, 165, 183, 0.1)'
          >
            <rect x='0' y='0' rx='7' ry='7' width='80' height='13' />
          </ContentLoader>
        </div>
      </div>
      <div className={classes.reporter}>
        <div className={classes.centeredSkeleton}>
          <ContentLoader
            title={''}
            speed={1.8}
            width={'80%'}
            height={13}
            backgroundColor='rgba(152, 165, 183, 0.3)'
            foregroundColor='rgba(152, 165, 183, 0.15)'
          >
            <rect x='0' y='0' rx='7' ry='7' width='80%' height='13' />
          </ContentLoader>
        </div>
      </div>
      <div className={classes.type}>
        <div className={classes.totalSkeletonWrap}>
          <div>
            <ContentLoader
              title={''}
              speed={1.8}
              width={25}
              height={24}
              backgroundColor='rgba(152, 165, 183, 0.2)'
              foregroundColor='rgba(152, 165, 183, 0.1)'
            >
              <rect x='0' y='0' rx='12' ry='12' width='24' height='24' />
            </ContentLoader>
          </div>
          <div>
            <ContentLoader
              title={''}
              speed={1.8}
              width={60}
              height={13}
              backgroundColor='rgba(152, 165, 183, 0.3)'
              foregroundColor='rgba(152, 165, 183, 0.15)'
            >
              <rect x='0' y='0' rx='7' ry='7' width='60' height='13' />
            </ContentLoader>
          </div>
        </div>
      </div>
      <div className={classes.actions} />
    </div>
  );
};
